'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';

import { Button } from '@kit/ui/button';
import { Trans } from '@kit/ui/trans';

const FONT_INTER = 'var(--font-inter-agency), -apple-system, sans-serif';

const CATALOG_LINKS = [
  { href: '/dancers', key: 'agency:nav.dancers', testId: 'nav-dancers' },
  { href: '/auditions', key: 'agency:nav.auditions', testId: 'nav-auditions' },
  {
    href: '/competitions',
    key: 'agency:nav.competitions',
    testId: 'nav-competitions',
  },
  { href: '/events', key: 'agency:nav.events', testId: 'nav-events' },
  {
    href: '/marketplace',
    key: 'agency:nav.marketplace',
    testId: 'nav-marketplace',
  },
] as const;

interface NavProps {
  /** 'dark-hero' (default — the /agency luxury page): white text over
   *  the full-bleed dark hero until scrolled past it, minimal link set.
   *  'light' (apex homepage, BAL-662 Phase 7 item 6): standard chrome
   *  on the pinned light theme — catalog links first-class, audience
   *  exits grouped right, mobile disclosure menu. */
  variant?: 'dark-hero' | 'light';
  /** Cross-brand Flow URL (resolved server-side via buildCrossBrandUrl). */
  flowUrl?: string;
}

/** The /agency luxury page nav — preserved behavior (formerly the only
 *  variant; Replit-exact constraint lifted by Antoine 2026-07-14, but
 *  this surface keeps its look). framer-motion fade dropped for a CSS
 *  transition — same visual, no motion library. */
function DarkHeroNav() {
  const [scrolled, setScrolled] = useState(false);
  const [overHero, setOverHero] = useState(true);

  useEffect(() => {
    const onScroll = () => {
      const vh = window.innerHeight;
      setScrolled(window.scrollY > 40);
      setOverHero(window.scrollY < vh * 0.85);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const textColor = overHero ? 'text-white/80' : 'text-foreground';
  const borderColor = overHero ? 'border-white/30' : 'border-foreground/30';
  const hoverBorder = overHero
    ? 'hover:border-white'
    : 'hover:border-foreground';
  const itemClass = `hidden border-b pb-px text-xs font-light tracking-[0.2em] uppercase transition-colors duration-700 sm:inline-block ${borderColor} ${hoverBorder}`;

  return (
    <nav
      className={`fixed top-0 right-0 left-0 z-50 flex items-center justify-between px-8 py-6 transition-all duration-700 md:px-16 ${
        scrolled && !overHero
          ? 'border-border bg-background/95 border-b backdrop-blur-sm'
          : ''
      }`}
    >
      <Link
        href="/"
        className={`text-xs font-light tracking-[0.25em] uppercase transition-colors duration-700 ${textColor}`}
        style={{ fontFamily: FONT_INTER }}
        data-testid="nav-logo"
      >
        {/* i18n-ignore brand name */}
        Ballee
      </Link>
      <div
        className={`flex items-center gap-5 transition-colors duration-700 ${textColor}`}
      >
        <Link
          href="/agency"
          className={itemClass}
          style={{ fontFamily: FONT_INTER }}
          data-testid="nav-agency"
        >
          <Trans i18nKey="agency:nav.agency" />
        </Link>
        <Link
          href="/explore"
          className={itemClass}
          style={{ fontFamily: FONT_INTER }}
          data-testid="nav-explore"
        >
          <Trans i18nKey="agency:nav.explore" />
        </Link>
        <a
          href="#contact"
          className={itemClass}
          style={{ fontFamily: FONT_INTER }}
          data-testid="nav-contact"
        >
          <Trans i18nKey="agency:nav.contact" />
        </a>
        <Link
          href="/auth/sign-in"
          className={itemClass}
          style={{ fontFamily: FONT_INTER }}
          data-testid="nav-login"
        >
          <Trans i18nKey="agency:nav.login" />
        </Link>
        <Link
          href="/auth/sign-up"
          className={`border px-3 py-1.5 text-xs font-light tracking-[0.2em] uppercase transition-all duration-700 ${borderColor} ${hoverBorder}`}
          style={{ fontFamily: FONT_INTER }}
          data-testid="nav-signup"
        >
          <Trans i18nKey="agency:nav.signup" />
        </Link>
      </div>
    </nav>
  );
}

/** Homepage nav (BAL-662): catalog links first-class, audience exits
 *  grouped right (AngelList/Later pattern from the Mobbin research),
 *  solid light chrome (the old white-over-hero logic rendered white
 *  text on the light homepage), and a mobile disclosure menu exposing
 *  every link below lg (a11y review 2026-07-14). */
function LightNav({ flowUrl }: { flowUrl?: string }) {
  const [open, setOpen] = useState(false);

  const catalogItem =
    'text-muted-foreground hover:text-foreground text-sm font-medium transition-colors';
  const exitItem =
    'text-muted-foreground hover:text-foreground hidden text-xs font-medium tracking-wide uppercase transition-colors xl:inline-block';

  return (
    <nav className="border-border bg-background/90 fixed top-0 right-0 left-0 z-50 border-b backdrop-blur-md">
      <div className="mx-auto flex h-14 max-w-7xl items-center gap-6 px-6">
        <Link
          href="/"
          className="text-foreground text-sm font-bold tracking-[0.2em] uppercase"
          data-testid="nav-logo"
        >
          {/* i18n-ignore brand name */}
          Ballee
        </Link>

        <div className="hidden items-center gap-5 lg:flex">
          {CATALOG_LINKS.map(({ href, key, testId }) => (
            <Link
              key={href}
              href={href}
              className={catalogItem}
              data-testid={testId}
            >
              <Trans i18nKey={key} />
            </Link>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-5">
          {flowUrl ? (
            <a href={flowUrl} className={exitItem} data-testid="nav-flow">
              <Trans i18nKey="agency:nav.forCompanies" />
            </a>
          ) : null}
          <Link href="/agency" className={exitItem} data-testid="nav-agency">
            <Trans i18nKey="agency:nav.forBrands" />
          </Link>
          <Link
            href="/auth/sign-in"
            className="text-muted-foreground hover:text-foreground hidden py-2 text-sm font-medium transition-colors sm:inline-block"
            data-testid="nav-login"
          >
            <Trans i18nKey="agency:nav.login" />
          </Link>
          <Link
            href="/auth/sign-up"
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
            data-testid="nav-signup"
          >
            <Trans i18nKey="agency:nav.joinFree" />
          </Link>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="lg:hidden"
            aria-expanded={open}
            aria-controls="nav-menu"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-menu-button"
          >
            <span className="sr-only">
              <Trans i18nKey="agency:nav.menu" />
            </span>
            <span aria-hidden="true">{open ? '✕' : '☰'}</span>
          </Button>
        </div>
      </div>

      {open ? (
        <div
          id="nav-menu"
          className="border-border bg-background border-t px-6 py-4 lg:hidden"
          data-testid="nav-menu"
        >
          <div className="flex flex-col gap-1">
            {CATALOG_LINKS.map(({ href, key }) => (
              <Link
                key={href}
                href={href}
                className="text-foreground py-2.5 text-base font-medium"
                onClick={() => setOpen(false)}
              >
                <Trans i18nKey={key} />
              </Link>
            ))}
            <div className="border-border mt-2 flex flex-col gap-1 border-t pt-3">
              {flowUrl ? (
                <a
                  href={flowUrl}
                  className="text-muted-foreground py-2.5 text-sm font-medium tracking-wide uppercase"
                >
                  <Trans i18nKey="agency:nav.forCompanies" />
                </a>
              ) : null}
              <Link
                href="/agency"
                className="text-muted-foreground py-2.5 text-sm font-medium tracking-wide uppercase"
                onClick={() => setOpen(false)}
              >
                <Trans i18nKey="agency:nav.forBrands" />
              </Link>
              <Link
                href="/auth/sign-in"
                className="text-muted-foreground py-2.5 text-sm font-medium"
                onClick={() => setOpen(false)}
              >
                <Trans i18nKey="agency:nav.login" />
              </Link>
            </div>
          </div>
        </div>
      ) : null}
    </nav>
  );
}

export function Nav({ variant = 'dark-hero', flowUrl }: NavProps) {
  return variant === 'light' ? <LightNav flowUrl={flowUrl} /> : <DarkHeroNav />;
}
